import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { API_ERRORS, type ApiErrorCode } from "@shared/contracts";
import { ApiError } from "@/app/lib/api-client";
import { reportError } from "@/app/observability";
import { PERSISTED_MAX_AGE } from "./query-persistence";

/**
 * ONE PLACE WHERE A FAILURE IS HANDLED, AND IT IS NOT THE CALL SITE.
 *
 * Every read and every write passes through these two caches, so this is the only file that
 * has to decide what happens when something fails: the user is told once, the error is
 * reported once, and a dead session signs out once rather than eleven times in parallel.
 * Handling failures per call site is how an app ends up showing three toasts for one dropped
 * request and reporting none of them.
 */

/**
 * A mutation that words its own failure opts out here. Typed, so a typo is a build error.
 *
 * There is no `operation` field any more. What the user was trying to do is DERIVED from the
 * cache key — `["notes", "list"]` is already `notes` / `list` — so the name in a report
 * cannot drift from the key that produced it and cannot be forgotten on a new hook. Every
 * key comes from the one registry (`no-inline-query-keys` sees `mutationKey` too), which is
 * what makes the first two segments dependable.
 */
declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: {
      /** This mutation shows its own message; the global handler stays silent. */
      ownsErrorMessage?: true;
    };
    queryMeta: {
      ownsErrorMessage?: true;
    };
  }
}

type Notifier = (code: ApiErrorCode, message?: string) => void;

/**
 * The app installs the real one (a toast, a banner) at start-up. Until it does, failures
 * still reach the console — silence would be worse.
 */
let notify: Notifier = (code) => console.warn(`[unhandled] ${code}`);

export function setErrorNotifier(notifier: Notifier) {
  notify = notifier;
}

/** Called when the session is genuinely gone. Set by the auth layer at start-up. */
let onSessionLost: () => void = () => {};

export function setSessionLostHandler(handler: () => void) {
  onSessionLost = handler;
}

function codeOf(error: unknown): ApiErrorCode {
  if (error && typeof error === "object" && "code" in error) {
    return (error as { code: ApiErrorCode }).code;
  }
  return API_ERRORS.SERVER_ERROR;
}

/**
 * WHAT THE USER WAS TRYING TO DO, read off the cache key instead of restated in `meta`.
 *
 * Every key in the registry is `[domain, action, ...]` — `["notes", "list"]`,
 * `["session", "current"]`. A mutation with no key reports without a name, which is exactly
 * what a forgotten `meta.operation` used to do, minus the second thing to keep in sync.
 */
function nameOf(key: readonly unknown[] | undefined): { domain?: string; action?: string } {
  const [domain, action] = key ?? [];
  return {
    domain: typeof domain === "string" ? domain : undefined,
    action: typeof action === "string" ? action : undefined,
  };
}

/**
 * THE SERVICE'S OWN WORDING, when there is any — and never a raw JavaScript message.
 *
 * A refusal the service explained ("this note was deleted while you were editing it") is
 * more use than the generic line for its code. Everything else falls back to the translated
 * vocabulary: a `TypeError` reading "Failed to fetch dynamically imported module:
 * /assets/notes-B7f3d.js" is true, useless, and frightening.
 *
 * `ApiError` sets `message` to the CODE when the service sent no prose, which is why that
 * case is excluded rather than shown as "SERVER_ERROR".
 */
function serviceWording(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  return error.message && error.message !== error.code ? error.message : undefined;
}

async function handle(
  error: unknown,
  meta: { ownsErrorMessage?: true } | undefined,
  key: readonly unknown[] | undefined,
) {
  const code = codeOf(error);

  if (code === API_ERRORS.UNAUTHENTICATED) {
    /**
     * A 401 FROM A SIGN-IN IS NOT A LOST SESSION — it is a wrong password.
     *
     * Only requests made WITH a session can prove one is gone. Treating a failed sign-in as
     * evidence sends the app off to re-check, confirm there is no session (there never was),
     * and clear the cache — which wipes the very error the form was about to show. The user
     * clicks "Sign in", nothing happens, and nothing explains why.
     *
     * A mutation that owns its message is exactly the set that must be excluded here: those
     * are the credential flows.
     */
    if (meta?.ownsErrorMessage) return;

    /**
     * A SINGLE 401 IS NOT PROOF THE SESSION IS DEAD.
     *
     * A phone changing cells, a proxy, a race with a token refresh — any of them can produce
     * one. Signing the user out on that is the difference between an app that occasionally
     * blips and an app that "logs me out at random", which is unfixable from inside a screen.
     * So the session is re-checked once, and only a confirmed loss signs out. Concurrent
     * failures collapse into that one check.
     */
    onSessionLost();
    return;
  }

  // Two exclusions, both deliberate. An unreachable network on a train is not a defect, and
  // a failure the transport layer already reported must not be reported twice — the flag is
  // carried on the envelope by api-client and copied onto this error by `unwrap`, which is
  // the other half of this single-authority rule.
  const alreadyReported = error instanceof ApiError && error.reported;
  if (code !== API_ERRORS.NETWORK_UNREACHABLE && !alreadyReported) {
    reportError(error, { code, ...nameOf(key) });
  }

  if (!meta?.ownsErrorMessage) notify(code, serviceWording(error));
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => void handle(error, query.meta, query.queryKey),
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) =>
      void handle(error, mutation.meta, mutation.options.mutationKey),
  }),
  defaultOptions: {
    queries: {
      /**
       * Phone-shaped defaults. A dropped request on a train must not become a permanently
       * empty screen, and a screen the user comes back to after ten minutes must not show
       * yesterday's data.
       */
      retry: (failureCount, error) => {
        const code = codeOf(error);
        // Retrying a refusal is pointless and, for a rate limit, actively harmful.
        const permanent: ApiErrorCode[] = [
          API_ERRORS.UNAUTHENTICATED,
          API_ERRORS.FORBIDDEN,
          API_ERRORS.NOT_FOUND,
          API_ERRORS.VALIDATION_FAILED,
          API_ERRORS.APP_TOO_OLD,
          API_ERRORS.RATE_LIMITED,
          /**
           * A CONFLICT MEANS SOMEBODY ELSE CHANGED THE ROW, and no number of retries can
           * make that untrue. Three attempts at 1s / 2s / 4s only delay the one message that
           * helps — reload and try again — by seven seconds of spinner.
           */
          API_ERRORS.CONFLICT,
        ];
        return !permanent.includes(code) && failureCount < 3;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 15_000),
      staleTime: 30_000,
      /**
       * LONG ENOUGH TO STILL BE THERE WHEN THE CACHE IS WRITTEN TO DISK.
       *
       * This used to be five minutes, which is a sensible number for a browser tab and the
       * wrong one once the cache is persisted: a query dropped from memory is dropped from
       * the next save too, so the disk copy would have held only whatever screen happened to
       * be open in the final five minutes of the session — and the cold start the next
       * morning would find nothing. It is deliberately the same constant the persisted copy
       * expires on, because two numbers that have to agree should be one number.
       */
      gcTime: PERSISTED_MAX_AGE,
      /**
       * REFETCH ON FOREGROUND, deliberately on. A phone app is backgrounded constantly and
       * comes back minutes or days later; without this the first thing the user sees is
       * whatever was true when they last closed it.
       */
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
    mutations: {
      // Writes are not retried by default: without an idempotency key on the far side, a
      // retried create is a duplicate. Individual mutations opt in when they are safe.
      retry: false,
    },
  },
});
